import moment from 'moment'
import {
  isCharterCancellationPolicy,
  showFlexibleCancellation,
} from 'lib/offer/cancellationPolicyUtils'
import {
  getFlexibleCancellationTimelineDetails,
  type FlexibleCancellationTimelineDetails,
} from './flexibleCancellationPolicyTimelineUtils'

export interface OfferFlexibleCancellationTimelineContext {
  details: FlexibleCancellationTimelineDetails
  isCharter: boolean
}

interface OfferFlexibleCancellationReferenceDateParams {
  cancellationPolicy?: App.OfferCancellationPolicy
  isCharterModeEnabled: boolean
  /** Hotel check-in date. Used when the policy is not charter. */
  checkInDate?: string | moment.Moment
  /**
   * Charter departure date. Used when charter policy applies
   * (same rule as checkout's getCheckoutCancellationPolicyTimelineView).
   */
  charterDepartDate?: string | moment.Moment
}

export interface OfferFlexibleCancellationReferenceDate {
  isCharter: boolean
  /** Charter → departure; otherwise hotel check-in. Callers must not collapse these fields. */
  timelineReferenceDate: string | moment.Moment | undefined
}

/**
 * Shared charter/check-in reference-date selection for offer flexible cancellation surfaces.
 */
export function getOfferFlexibleCancellationReferenceDate(
  params: OfferFlexibleCancellationReferenceDateParams,
): OfferFlexibleCancellationReferenceDate {
  const {
    cancellationPolicy,
    isCharterModeEnabled,
    checkInDate,
    charterDepartDate,
  } = params

  const isCharter = isCharterModeEnabled && isCharterCancellationPolicy(cancellationPolicy)

  return {
    isCharter,
    timelineReferenceDate: isCharter ? charterDepartDate : checkInDate,
  }
}

interface GetOfferFlexibleCancellationTimelineContextParams {
  cancellationPolicy?: App.OfferCancellationPolicy
  propertyTimezoneOffset?: number
  /** Hotel check-in date. Used as the timeline reference when the policy is not charter. */
  checkInDate?: string | moment.Moment
  /**
   * Charter departure date. Used as the timeline reference when charter policy applies
   * (same rule as checkout's getCheckoutCancellationPolicyTimelineView).
   */
  charterDepartDate?: string | moment.Moment
  purchaseDate?: string
  isCharterModeEnabled: boolean
  availableForPurchase?: boolean
  /**
   * Optional precomputed charter/check-in selection. Callers that already resolved the
   * reference date (e.g. getFlashCancellationPolicyView) should pass it to avoid a second pass.
   */
  referenceDate?: OfferFlexibleCancellationReferenceDate
}

/**
 * Shared eligibility + details assembly for offer-page flexible cancellation surfaces
 * (USP modal path and the desktop summary timeline experiment).
 *
 * Reference-date selection matches checkout: charter policies use departure date; otherwise check-in.
 * Callers must pass hotel check-in and charter departure as separate fields — do not collapse them.
 */
export function getOfferFlexibleCancellationTimelineContext(
  params: GetOfferFlexibleCancellationTimelineContextParams,
): OfferFlexibleCancellationTimelineContext | undefined {
  const {
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    charterDepartDate,
    purchaseDate,
    isCharterModeEnabled,
    availableForPurchase,
    referenceDate: referenceDateOverride,
  } = params

  if (!availableForPurchase || !cancellationPolicy) {
    return undefined
  }

  const { isCharter, timelineReferenceDate } = referenceDateOverride ?? getOfferFlexibleCancellationReferenceDate({
    cancellationPolicy,
    isCharterModeEnabled,
    checkInDate,
    charterDepartDate,
  })

  // Charter + selected hotel dates but departure still resolving: hide rather than flash generic copy.
  if (isCharter && checkInDate && !charterDepartDate) {
    return undefined
  }

  if (!showFlexibleCancellation(timelineReferenceDate, false, cancellationPolicy)) {
    return undefined
  }

  const details = getFlexibleCancellationTimelineDetails({
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate: timelineReferenceDate,
    purchaseDate,
  })

  // Match checkout: hide dated timelines once the flexible window has already passed.
  if (
    details.checkInDate &&
    details.startBasedOnCheckIn?.isBefore(moment(details.today).startOf('day'))
  ) {
    return undefined
  }

  return {
    details,
    isCharter,
  }
}
